/**
 * parts.tsx — byggeklosser til inDomain-illustrasjonene (isometrisk SVG)
 *
 * AddressBar: adressefelt med lås, tekst og blått «endelse»-felt. Globe: klode
 * på rund sokkel. PriceTag: blå prislapp med hull. Kun blå og grå; ingen
 * varemerker eller faktiske domenenavn.
 */

import { useId } from 'react';
import { Box, iso as heroIso } from '@/components/product/iso';
import styles from '@/components/product/illustrations.module.css';

type Project = (x: number, y: number, z: number) => string;

interface PartProps {
  x: number;
  y: number;
  z?: number;
  project?: Project;
}

export function AddressBar({ x, y, z = 0, project = heroIso }: PartProps) {
  const w = 4.4;
  const d = 1.3;
  const h = 0.28;
  const zt = z + h;
  const p = (u: number, v: number) => project(x + u * w, y + v * d, zt);
  const rect = (u0: number, v0: number, u1: number, v1: number, fill: string, key: string, opacity = 1) => (
    <polygon key={key} points={`${p(u0, v0)} ${p(u1, v0)} ${p(u1, v1)} ${p(u0, v1)}`} fill={fill} opacity={opacity} />
  );
  return (
    <g>
      <Box x={x} y={y} z={z} w={w} d={d} h={h} colors={['#ffffff', '#e0e0e0', '#c6c6c6']} project={project} />
      {rect(0.04, 0.25, 0.1, 0.75, '#0f62fe', 'lock')}
      {rect(0.15, 0.4, 0.5, 0.6, '#8d8d8d', 'name', 0.85)}
      {rect(0.52, 0.28, 0.64, 0.72, '#0f62fe', 'tld')}
      {rect(0.68, 0.32, 0.7, 0.68, '#393939', 'cursor')}
    </g>
  );
}

/** Klode (skjermkoordinater rundt cx, cy) på rund sokkel */
export function Globe({ cx, cy }: { cx: number; cy: number }) {
  const uid = useId();
  const grad = `${uid}-globe`;
  return (
    <g transform={`translate(${cx} ${cy})`}>
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#78a9ff" />
          <stop offset="1" stopColor="#0f62fe" />
        </linearGradient>
      </defs>
      <ellipse className={styles.left} cx="0" cy="14" rx="62" ry="28" />
      <ellipse className={styles.top} cx="0" cy="6" rx="62" ry="28" />
      <circle cx="0" cy="-34" r="44" fill={`url(#${grad})`} />
      <g fill="none" stroke="#ffffff" strokeWidth="2" opacity="0.5">
        <ellipse cx="0" cy="-34" rx="44" ry="14" />
        <ellipse cx="0" cy="-34" rx="18" ry="44" />
        <line x1="0" y1="-78" x2="0" y2="10" />
        <ellipse cx="0" cy="-34" rx="44" ry="30" />
      </g>
    </g>
  );
}

/** Prislapp: blå flate med hull og to linjer */
export function PriceTag({ x, y, z = 0, project = heroIso }: PartProps) {
  const w = 2.4;
  const d = 1.7;
  const h = 0.22;
  const zt = z + h;
  const p = (u: number, v: number) => project(x + u * w, y + v * d, zt);
  const rect = (u0: number, v0: number, u1: number, v1: number, fill: string, key: string, opacity = 1) => (
    <polygon key={key} points={`${p(u0, v0)} ${p(u1, v0)} ${p(u1, v1)} ${p(u0, v1)}`} fill={fill} opacity={opacity} />
  );
  return (
    <g>
      <Box x={x} y={y} z={z} w={w} d={d} h={h} colors={['#4589ff', '#0f62fe', '#0043ce']} project={project} />
      {rect(0.07, 0.38, 0.17, 0.62, '#0043ce', 'hole')}
      {rect(0.3, 0.3, 0.88, 0.44, '#ffffff', 'l1', 0.9)}
      {rect(0.3, 0.56, 0.7, 0.7, '#ffffff', 'l2', 0.7)}
    </g>
  );
}
